import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const API_URL = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_PROD_BACKEND + "review/" : process.env.REACT_APP_DEV_BACKEND + "review/";

export const reviewApi = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: API_URL,
        prepareHeaders: (headers, { getState }) => {
            const user = JSON.parse(localStorage.getItem("user"));
            if (user && user.accessToken) {
                headers.set("authorization", user.accessToken);
            }
            return headers;
        },
    }),
    reducerPath: "reviewApi",
    tagTypes: ["Reviews", "ReviewMembers", "ReviewImports", "ReviewInvites", "ReviewQueue", "ReviewProgress", "ReviewMyDecisions", "ReviewConflicts", "ReviewDashboard", "ReviewStudyGroups"],
    endpoints: (build) => ({
        getMyReviews: build.query({
            query: () => "mine",
            providesTags: ["Reviews"],
        }),
        getReview: build.query({
            query: (reviewId) => `${reviewId}`,
            providesTags: (result, error, reviewId) => [{ type: "Reviews", id: reviewId }],
        }),
        getMyInvites: build.query({
            query: () => "invites/mine",
            providesTags: ["ReviewInvites"],
        }),
        getMembers: build.query({
            query: (reviewId) => `${reviewId}/members`,
            providesTags: (result, error, reviewId) => [{ type: "ReviewMembers", id: reviewId }],
        }),
        searchReviewers: build.query({
            query: ({ reviewId, q }) => ({
                url: `${reviewId}/members/search`,
                method: "GET",
                params: { q },
            }),
        }),
        getImports: build.query({
            query: (reviewId) => `${reviewId}/imports`,
            providesTags: (result, error, reviewId) => [{ type: "ReviewImports", id: reviewId }],
        }),
        createReview: build.mutation({
            query: (formData) => ({
                url: "",
                method: "POST",
                body: formData,
            }),
            invalidatesTags: ["Reviews"],
        }),
        updateReviewSettings: build.mutation({
            query: ({ reviewId, ...patch }) => ({
                url: `${reviewId}`,
                method: "PATCH",
                body: patch,
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "Reviews", id: reviewId }, "Reviews"],
        }),
        toggleBlinding: build.mutation({
            query: ({ reviewId, stage, value }) => ({
                url: `${reviewId}/blinding`,
                method: "PATCH",
                body: { stage, value },
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "Reviews", id: reviewId }],
        }),
        archiveReview: build.mutation({
            query: (reviewId) => ({
                url: `${reviewId}/archive`,
                method: "PATCH",
            }),
            invalidatesTags: (result, error, reviewId) => [{ type: "Reviews", id: reviewId }, "Reviews"],
        }),
        addImport: build.mutation({
            query: ({ reviewId, formData }) => ({
                url: `${reviewId}/imports`,
                method: "POST",
                body: formData,
            }),
            invalidatesTags: (result, error, { reviewId }) => [
                { type: "ReviewImports", id: reviewId },
                { type: "Reviews", id: reviewId },
            ],
        }),
        inviteMember: build.mutation({
            query: ({ reviewId, userId }) => ({
                url: `${reviewId}/members`,
                method: "POST",
                body: { userId },
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "ReviewMembers", id: reviewId }],
        }),
        setMemberRole: build.mutation({
            query: ({ reviewId, memberId, role }) => ({
                url: `${reviewId}/members/${memberId}/role`,
                method: "PATCH",
                body: { role },
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "ReviewMembers", id: reviewId }],
        }),
        removeMember: build.mutation({
            query: ({ reviewId, memberId }) => ({
                url: `${reviewId}/members/${memberId}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "ReviewMembers", id: reviewId }],
        }),
        acceptInvite: build.mutation({
            query: (memberId) => ({
                url: `invites/${memberId}/accept`,
                method: "PATCH",
            }),
            invalidatesTags: ["ReviewInvites", "Reviews"],
        }),
        getQueue: build.query({
            query: ({ reviewId, stage, page = 0, pageSize = 20 }) => ({
                url: `${reviewId}/queue/${stage}`,
                method: "GET",
                params: { page, pageSize },
            }),
            providesTags: (result, error, { reviewId, stage }) => [{ type: "ReviewQueue", id: `${reviewId}-${stage}` }],
        }),
        getProgress: build.query({
            query: (reviewId) => `${reviewId}/progress`,
            providesTags: (result, error, reviewId) => [{ type: "ReviewProgress", id: reviewId }],
        }),
        getMyDecisions: build.query({
            query: ({ reviewId, stage, page = 0, pageSize = 20, q }) => ({
                url: `${reviewId}/my-decisions/${stage}`,
                method: "GET",
                params: { page, pageSize, ...(q ? { q } : {}) },
            }),
            providesTags: (result, error, { reviewId, stage }) => [{ type: "ReviewMyDecisions", id: `${reviewId}-${stage}` }],
        }),
        submitDecision: build.mutation({
            query: ({ reviewId, stage, recordId, value, reason, note }) => ({
                url: `${reviewId}/decisions/${stage}`,
                method: "POST",
                body: { recordId, value, reason, note },
            }),
            // Broad invalidation: re-deciding a record from the "My decisions"
            // editor (not just the live swipe queue) can move counts on the
            // dashboard, the PRISMA diagram, and later-stage queues too.
            invalidatesTags: (result, error, { reviewId, stage }) => [
                { type: "ReviewProgress", id: reviewId },
                { type: "ReviewMyDecisions", id: `${reviewId}-${stage}` },
                { type: "ReviewQueue", id: `${reviewId}-${stage}` },
                { type: "ReviewQueue", id: `${reviewId}-${stage + 1}` },
                { type: "ReviewDashboard", id: reviewId },
                { type: "ReviewStudyGroups", id: reviewId },
                { type: "ReviewConflicts", id: `${reviewId}-3` },
            ],
        }),
        undoDecision: build.mutation({
            query: ({ reviewId, stage }) => ({
                url: `${reviewId}/decisions/${stage}/undo`,
                method: "POST",
            }),
            invalidatesTags: (result, error, { reviewId, stage }) => [
                { type: "ReviewQueue", id: `${reviewId}-${stage}` },
                { type: "ReviewProgress", id: reviewId },
                { type: "ReviewMyDecisions", id: `${reviewId}-${stage}` },
            ],
        }),
        setStageLock: build.mutation({
            query: ({ reviewId, stage, locked }) => ({
                url: `${reviewId}/stage-lock`,
                method: "PATCH",
                body: { stage, locked },
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "Reviews", id: reviewId }],
        }),
        getUnpaywallLink: build.query({
            query: ({ reviewId, recordId }) => `${reviewId}/records/${recordId}/unpaywall`,
        }),
        setFullTextUrl: build.mutation({
            // No cache invalidation: the caller patches its local card state
            // directly from the response. Invalidating the queue tag would
            // refetch page 0 and reset the reviewer's position in the stack.
            query: ({ reviewId, recordId, url }) => ({
                url: `${reviewId}/records/${recordId}/full-text-url`,
                method: "PATCH",
                body: { url },
            }),
        }),
        addRecordNote: build.mutation({
            query: ({ reviewId, recordId, text }) => ({
                url: `${reviewId}/records/${recordId}/notes`,
                method: "POST",
                body: { text },
            }),
        }),
        getConflicts: build.query({
            query: ({ reviewId, stage = 3, resolved }) => ({
                url: `${reviewId}/conflicts`,
                method: "GET",
                params: { stage, ...(resolved !== undefined ? { resolved } : {}) },
            }),
            providesTags: (result, error, { reviewId, stage = 3 }) => [{ type: "ReviewConflicts", id: `${reviewId}-${stage}` }],
        }),
        resolveConflict: build.mutation({
            query: ({ reviewId, recordId, value, reason }) => ({
                url: `${reviewId}/conflicts/${recordId}/resolve`,
                method: "POST",
                body: { value, reason },
            }),
            invalidatesTags: (result, error, { reviewId }) => [
                { type: "ReviewConflicts", id: `${reviewId}-3` },
                { type: "ReviewDashboard", id: reviewId },
            ],
        }),
        getDashboard: build.query({
            query: (reviewId) => `${reviewId}/dashboard`,
            providesTags: (result, error, reviewId) => [{ type: "ReviewDashboard", id: reviewId }],
        }),
        getBibliographyList: build.query({
            query: (reviewId) => `${reviewId}/exports/bibliography`,
        }),
        getPrismaCounts: build.query({
            query: (reviewId) => `${reviewId}/exports/prisma`,
            providesTags: (result, error, reviewId) => [{ type: "ReviewStudyGroups", id: reviewId }],
        }),
        getStudyGroups: build.query({
            query: (reviewId) => `${reviewId}/study-groups`,
            providesTags: (result, error, reviewId) => [{ type: "ReviewStudyGroups", id: reviewId }],
        }),
        createStudyGroup: build.mutation({
            query: ({ reviewId, label, recordIds }) => ({
                url: `${reviewId}/study-groups`,
                method: "POST",
                body: { label, recordIds },
            }),
            // Also invalidates getPrismaCounts (same tag): grouping changes
            // the "studies included" count on the PRISMA diagram too.
            invalidatesTags: (result, error, { reviewId }) => [{ type: "ReviewStudyGroups", id: reviewId }],
        }),
        deleteStudyGroup: build.mutation({
            query: ({ reviewId, groupId }) => ({
                url: `${reviewId}/study-groups/${groupId}`,
                method: "DELETE",
            }),
            invalidatesTags: (result, error, { reviewId }) => [{ type: "ReviewStudyGroups", id: reviewId }],
        }),
    }),
});

export const {
    useGetMyReviewsQuery,
    useGetReviewQuery,
    useGetMyInvitesQuery,
    useGetMembersQuery,
    useSearchReviewersQuery,
    useGetImportsQuery,
    useCreateReviewMutation,
    useUpdateReviewSettingsMutation,
    useToggleBlindingMutation,
    useArchiveReviewMutation,
    useAddImportMutation,
    useInviteMemberMutation,
    useSetMemberRoleMutation,
    useRemoveMemberMutation,
    useAcceptInviteMutation,
    useGetQueueQuery,
    useGetProgressQuery,
    useGetMyDecisionsQuery,
    useSubmitDecisionMutation,
    useUndoDecisionMutation,
    useSetStageLockMutation,
    useGetUnpaywallLinkQuery,
    useSetFullTextUrlMutation,
    useAddRecordNoteMutation,
    useGetConflictsQuery,
    useResolveConflictMutation,
    useGetDashboardQuery,
    useGetBibliographyListQuery,
    useGetPrismaCountsQuery,
    useGetStudyGroupsQuery,
    useCreateStudyGroupMutation,
    useDeleteStudyGroupMutation,
} = reviewApi;
